export const TOTAL_SLOTS = 96;
const MINUTES_PER_SLOT = 15;
const MINUTES_PER_DAY = 24 * 60;

const clampMinutes = (minutes: number) =>
  Math.max(0, Math.min(MINUTES_PER_DAY, minutes));

export const createEmptySelection = () =>
  Array.from({ length: TOTAL_SLOTS }, () => false);

const getRanges = (selected: boolean[]) => {
  if (selected.length !== TOTAL_SLOTS) {
    throw new Error("Selection must contain exactly 96 values.");
  }

  const ranges: number[][] = [];
  let startIndex: number | null = null;

  for (let i = 0; i < selected.length; i++) {
    if (selected[i] && startIndex === null) {
      startIndex = i;
    } else if (!selected[i] && startIndex !== null) {
      ranges.push([startIndex, i]);
      startIndex = null;
    }
  }

  if (startIndex !== null) {
    ranges.push([startIndex, selected.length]);
  }

  return ranges;
};

export const hasInvalidSelection = (selected: boolean[]) => {
  const ranges = getRanges(selected);
  return (
    ranges.length > 2 ||
    (ranges.length === 2 && (ranges[0][0] !== 0 || ranges[1][1] !== TOTAL_SLOTS))
  );
};

export const getRangeFromSelection = (selected: boolean[]) => {
  const ranges = getRanges(selected);
  if (!ranges.length) {
    return null;
  }

  if (ranges.length === 1) {
    return {
      startMinutes: ranges[0][0] * MINUTES_PER_SLOT,
      endMinutes: ranges[0][1] * MINUTES_PER_SLOT,
    };
  }

  if (ranges.length === 2 && ranges[0][0] === 0 && ranges[1][1] === TOTAL_SLOTS) {
    return {
      startMinutes: ranges[1][0] * MINUTES_PER_SLOT,
      endMinutes: ranges[0][1] * MINUTES_PER_SLOT,
    };
  }

  return null;
};

export const roundToSlot = (minutes: number, mode: "floor" | "ceil") => {
  const clamped = clampMinutes(minutes);
  const slotValue = clamped / MINUTES_PER_SLOT;
  const rounded = mode === "floor" ? Math.floor(slotValue) : Math.ceil(slotValue);
  return clampMinutes(rounded * MINUTES_PER_SLOT);
};

export const minutesFromDate = (value: Date) =>
  value.getHours() * 60 + value.getMinutes();

export const dateFromMinutes = (baseDate: Date, minutes: number) => {
  const date = new Date(baseDate);
  const dayOffset = Math.floor(minutes / MINUTES_PER_DAY);
  const minuteOffset = minutes % MINUTES_PER_DAY;
  date.setDate(date.getDate() + dayOffset);
  date.setHours(Math.floor(minuteOffset / 60), minuteOffset % 60, 0, 0);
  return date;
};

export const minutesFromApiTime = (time: string) => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

export const isCalendarEventAlwaysOn = (
  startTime: string | null | undefined,
  endTime: string | null | undefined
) => {
  if (!startTime || !endTime) {
    return true;
  }

  return startTime === "00:00:00" && endTime === "23:59:59";
};

export const normalizeCalendarEventTimes = (
  startTime: string | null | undefined,
  endTime: string | null | undefined
) => {
  if (isCalendarEventAlwaysOn(startTime, endTime)) {
    return {
      start_time: "00:00:00",
      end_time: "23:59:59",
    };
  }

  return {
    start_time: startTime ?? null,
    end_time: endTime ?? null,
  };
};

export const parseCalendarEventDate = (dateString: string) => {
  const parts = dateString.split("-");
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

export const formatCalendarEventTimeForAPI = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
};

const formatCalendarEventTimeForDisplay = (time: string) => {
  const [hours = "00", minutes = "00"] = time.split(":");
  return `${hours}:${minutes}`;
};

export const formatCalendarEventTimeRangeDisplay = (
  startTime: string | null | undefined,
  endTime: string | null | undefined
) => {
  if (isCalendarEventAlwaysOn(startTime, endTime)) {
    return "Always On";
  }

  return `${formatCalendarEventTimeForDisplay(startTime!)}-${formatCalendarEventTimeForDisplay(endTime!)}`;
};

export const getCalendarEventTimesForAPI = (
  detectionsAlwaysOn: boolean,
  startTime: Date | null,
  endTime: Date | null
): { start_time: string | null; end_time: string | null } => {
  if (detectionsAlwaysOn) {
    return {
      start_time: "00:00:00",
      end_time: "23:59:59",
    };
  }

  if (!startTime || !endTime) {
    return {
      start_time: null,
      end_time: null,
    };
  }

  return {
    start_time: formatCalendarEventTimeForAPI(startTime),
    end_time: formatCalendarEventTimeForAPI(endTime),
  };
};

export const createSelectionFromTimes = (startMinutes: number, endMinutes: number) => {
  const normalizedStart = roundToSlot(startMinutes, "floor");
  const normalizedEnd = roundToSlot(endMinutes, "ceil");
  const selected = createEmptySelection();

  const startSlot = normalizedStart / MINUTES_PER_SLOT;
  const endSlot = normalizedEnd / MINUTES_PER_SLOT;

  if (startSlot === endSlot) {
    return selected;
  }

  if (startSlot < endSlot) {
    for (let i = startSlot; i < endSlot; i++) {
      selected[i] = true;
    }
    return selected;
  }

  for (let i = startSlot; i < TOTAL_SLOTS; i++) {
    selected[i] = true;
  }
  for (let i = 0; i < endSlot; i++) {
    selected[i] = true;
  }

  return selected;
};
